import { z } from 'zod';
import { UAE_PHONE_REGEX } from './phone';

// Strips spaces, dashes and parentheses, then tests UAE mobile/landline patterns.
function isUaePhone(value: string): boolean {
  const normalized = value.replace(/[\s\-()+]/g, '');
  // With country code: 9715XXXXXXXX (mobile) or 971[234679]XXXXXXX (landline)
  // Local:             05XXXXXXXX          or  0[234679]XXXXXXX
  return /^971(5\d{8}|[234679]\d{7})$|^0(5\d{8}|[234679]\d{7})$/.test(normalized);
}

export const uaePhoneOptional = z.string().refine((v) => !v || isUaePhone(v), {
  message: 'Enter a valid UAE phone number (e.g. +971 50 123 4567 or 050 123 4567)',
});

export const uaePhoneRequired = z.string().min(1, 'Phone number is required').refine(isUaePhone, {
  message: 'Enter a valid UAE phone number (e.g. +971 50 123 4567 or 050 123 4567)',
});

// Canonical User.phoneNumber format (+971 + 9-digit mobile subscriber
// number) — used for login/profile/admin-user phone fields, which lock to
// a single format so the same number can't be registered twice under
// different spellings. See BPS-0176.
export const uaeMobileOptional = z.string().refine((v) => !v || UAE_PHONE_REGEX.test(v), {
  message: 'Enter a valid UAE mobile number',
});

export const emailRequired = z
  .string()
  .min(1, 'Email address is required')
  .email('Enter a valid email address');

export const emailOptional = z
  .string()
  .refine((v) => !v || z.string().email().safeParse(v).success, {
    message: 'Enter a valid email address',
  });

// ── Reusable contact schema ────────────────────────────────────────────────────

export const contactSchema = z.object({
  id: z.string().optional(),
  fullName: z.string().min(1, 'Full name is required'),
  email: emailOptional,
  phone: uaePhoneOptional,
  designation: z.string().optional(),
  isPrimary: z.boolean(),
});

export type ContactFormValues = z.infer<typeof contactSchema>;

// ── Customer org-info schema ───────────────────────────────────────────────────

export const customerOrgSchema = z.object({
  name: z.string().min(2, 'Organization name must be at least 2 characters'),
  sapCode: z.string().optional(),
  accountType: z.string().optional(),
  primaryEmail: emailOptional,
  primaryPhone: uaePhoneOptional,
  tradeLicenseNo: z.string().optional(),
  vatRegistrationNo: z.string().optional(),
  contacts: z.array(contactSchema),
});

export type CustomerOrgFormValues = z.infer<typeof customerOrgSchema>;

// ── User creation schema ───────────────────────────────────────────────────────

export const newUserSchema = z.object({
  fullName: z.string().min(1, 'Full name is required'),
  email: emailRequired,
  username: z
    .string()
    .refine((v) => !v || /^[a-z0-9_.-]+$/.test(v), {
      message: 'Only lowercase letters, numbers, _ . - allowed',
    })
    .optional(),
  phone: uaeMobileOptional,
  employeeId: z.string().optional(),
  department: z.string().optional(),
  password: z.string().optional(),
  sendInvite: z.boolean(),
  forceReset: z.boolean(),
});

export type NewUserFormValues = z.infer<typeof newUserSchema>;

// ── Permit creation form schema ────────────────────────────────────────────────

export const permitFormSchema = z
  .object({
    accountId: z.string().min(1, 'Account is required'),
    accountName: z.string(),
    contactId: z.string().min(1, 'Contact is required'),
    contactName: z.string(),
    permitTypeId: z.string(),
    permitTypeCode: z.string().min(1, 'Select a permit type'),
    requestType: z.string(),
    facilitySubType: z.string(),
    riskCode: z.string(),
    riskId: z.string(),
    riskCategoryTotalFee: z.number(),
    permissionDate: z.string(),
    expiryDate: z.string(),
    permitActivity: z.string(),
    envPermitGeneralComments: z.string(),
    licenseType: z.string(),
    isRenewalOfId: z.string(),
    vehicleCount: z.number(),
    vehicleLicenses: z.array(z.object({ licenseNumber: z.string(), placeOfIssue: z.string() })),
    wasteClassification: z.string(),
    wasteType: z.string(),
    wasteQuantity: z.string(),
    landfillLocation: z.string(),
  })
  .superRefine((data, ctx) => {
    if (data.permitTypeCode && data.permitTypeCode !== 'WASTE' && !data.requestType)
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Request type is required',
        path: ['requestType'],
      });
    if (data.permitTypeCode === 'ENV') {
      if (!data.riskCode)
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Risk category is required',
          path: ['riskCode'],
        });
      if (!data.permissionDate)
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Permission date is required',
          path: ['permissionDate'],
        });
      if (!data.expiryDate)
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Expiry date is required',
          path: ['expiryDate'],
        });
    }
    if (data.permitTypeCode === 'WASTE') {
      if (!data.riskCode)
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Category is required',
          path: ['riskCode'],
        });
      if (!data.permissionDate)
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Permit date is required',
          path: ['permissionDate'],
        });
      if (data.expiryDate && data.permissionDate && data.expiryDate < data.permissionDate)
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Expiry date cannot be earlier than permit date',
          path: ['expiryDate'],
        });
      if (data.wasteQuantity && Number(data.wasteQuantity) <= 0)
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Quantity must be greater than zero',
          path: ['wasteQuantity'],
        });
    }
    if (data.permitTypeCode === 'VEH') {
      if (!data.riskCode)
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Category is required',
          path: ['riskCode'],
        });
      if (!data.permissionDate)
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Permission date is required',
          path: ['permissionDate'],
        });
      if (data.expiryDate && data.permissionDate && data.expiryDate < data.permissionDate)
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Expiry date cannot be earlier than permission date',
          path: ['expiryDate'],
        });
      if (!data.vehicleLicenses.some((l) => l.licenseNumber.trim()))
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'At least one license number is required',
          path: ['vehicleLicenses'],
        });
    }
  });

export type PermitFormValues = z.infer<typeof permitFormSchema>;
